// Chart of accounts used by the association's bookkeeper in QuickBooks
// (Villas de Costa Dorada I). Expense.category stores the account code;
// legacy free-text values pass through expenseCategoryLabel unchanged.

export interface AccountCategory {
  code: string;
  es: string;
  en: string;
}

export const EXPENSE_CATEGORIES: AccountCategory[] = [
  { code: "500", es: "Nómina", en: "Payroll expenses" },
  { code: "501", es: "Salarios", en: "Salaries" },
  { code: "502", es: "Vacaciones", en: "Vacations" },
  { code: "503", es: "Licencia por enfermedad", en: "Sick leave" },
  { code: "504", es: "Bono", en: "Bonus" },
  { code: "505", es: "Días feriados", en: "Holidays" },
  { code: "571", es: "Aguinaldo", en: "Aguinaldo (Christmas bonus)" },
  { code: "500-1", es: "Impuestos y beneficios de nómina", en: "Payroll taxes and benefits" },
  { code: "510", es: "Seguro Social", en: "Social Security" },
  { code: "511", es: "Seguro de incapacidad", en: "Disability insurance" },
  { code: "512", es: "Desempleo PR", en: "PR unemployment" },
  { code: "560", es: "Mantenimiento de áreas comunes", en: "Maintenance – common areas" },
  { code: "519", es: "Servicios de administración", en: "Administration services" },
  { code: "70500", es: "Penalidades y recargos", en: "Penalties and surcharges" },
];

// "560" -> "560 · Mantenimiento de áreas comunes"; unknown values render as-is.
export function expenseCategoryLabel(value: string, lang: "es" | "en"): string {
  const match = EXPENSE_CATEGORIES.find((c) => c.code === value);
  return match ? `${match.code} · ${match[lang]}` : value;
}

// The association's fiscal year runs August–July (e.g. May 2026 -> "2025-2026").
export function fiscalYearFor(year: number, month: number): string {
  return month >= 8 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
}

// Late charge on overdue maintenance fees (QuickBooks account 418A), per
// Reglamento Art. IX: a 30-day grace period, then a flat 10% surcharge, plus
// an additional 1% for every 30-day period of delinquency beyond the third
// (i.e. month 4 = 11%, month 5 = 12%, uncapped).
export const LATE_FEE_RATE = 0.1;
export const LATE_FEE_GRACE_PERIOD_DAYS = 30;
export const LATE_FEE_ESCALATION_THRESHOLD_PERIODS = 3;
export const LATE_FEE_ESCALATION_RATE_PER_PERIOD = 0.01;

export function computeLateFeeRate(daysLate: number): number {
  if (daysLate < LATE_FEE_GRACE_PERIOD_DAYS) return 0;
  const periodsLate = Math.floor(daysLate / LATE_FEE_GRACE_PERIOD_DAYS);
  const escalationPeriods = Math.max(0, periodsLate - LATE_FEE_ESCALATION_THRESHOLD_PERIODS);
  return LATE_FEE_RATE + escalationPeriods * LATE_FEE_ESCALATION_RATE_PER_PERIOD;
}
